import type { ToolSelection } from './types'
import { actionPolicy } from './action-policy'
import { inputSchema, ToolInputSchema } from './tool-configuration'

type Translate = (key: string) => string

export type CapabilityGroup = {
    key: string
    connectionId: string
    name?: string
    tools: ToolSelection[]
}

export type FixedSetting = {
    key: string
    title?: string
    value: unknown
}

const timeZoneFields = ['timezone', 'time_zone']

const dateFields = [
    'start_datetime',
    'end_datetime',
    'start_date',
    'end_date',
    'date',
    'time_min',
    'time_max',
    'start',
    'end',
]

// Field name parts that point to a resource, such as calendar_id or search_query.
const resourceWords = [
    'id',
    'ids',
    'query',
    'filter',
    'search',
    'criteria',
    'expression',
    'formula',
    'url',
    'uri',
    'path',
    'resource',
    'account',
    'calendar',
    'table',
    'database',
    'board',
    'folder',
    'items',
]

const resourceFieldPattern = new RegExp(`(^|_)(${resourceWords.join('|')})($|_)`)

const limitFieldPattern = /(max|min|limit|count)$/

// A date and time without an offset, for example 2025-01-31T09:30.
const naiveDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/

// These labels describe known field names, not inferred permissions or data sensitivity.
const fieldLabels: Record<string, string> = {
    calendar_id: 'Calendar',
    items: 'Resources',
    timezone: 'Time zone',
    time_zone: 'Time zone',
    time_min: 'Start time',
    time_max: 'End time',
    start_datetime: 'Start time',
    end_datetime: 'End time',
    start: 'Start',
    end: 'End',
    date_time: 'Date and time',
    time_zone_name: 'Time zone',
    event_duration_minutes: 'Duration in minutes',
    event_duration_hour: 'Duration in hours',
    duration_minutes: 'Duration in minutes',
    duration: 'Duration',
    summary: 'Title',
    title: 'Title',
    subject: 'Subject',
    attendees: 'Guests',
    description: 'Description',
    free: 'Free times',
    busy: 'Busy times',
    calendars: 'Calendars',
    is_reliable: 'Reliability',
    status: 'Status',
    html_link: 'Web link',
    send_updates: 'Send updates',
    exclude_organizer: 'Exclude organizer',
    create_meeting_room: 'Create meeting room',
    location: 'Location',
}

export function conversationFields(tool: ToolSelection) {
    const fixed = JSON.parse(tool.fixedArgumentsJson) as Record<string, unknown>

    return actionPolicy(tool).dynamic_arguments.filter((field) => !Object.prototype.hasOwnProperty.call(fixed, field))
}

// Display groups never affect execution, access approval, or action identity.
export function capabilityGroups(tools: ToolSelection[]): CapabilityGroup[] {
    const groups = new Map<string, CapabilityGroup>()

    for (const tool of tools) {
        const name = tool.capabilityName?.trim() || undefined
        const key = name ? `goal:${tool.connectionId}:${name}` : `action:${tool.connectionId}:${tool.slug}`
        const group = groups.get(key) ?? {
            key,
            connectionId: tool.connectionId,
            name,
            tools: [],
        }

        group.tools.push(tool)
        groups.set(key, group)
    }

    return [...groups.values()]
}

export function words(name: string) {
    return name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
}

// "startDateTime", "start-date-time" and "start_date_time" all become "start_date_time".
function normalizedField(name: string) {
    return words(name).toLowerCase().replace(/ /g, '_')
}

// Output paths are JSON Pointers, which escape "/" as "~1" and "~" as "~0".
function unescapePointer(part: string) {
    return part.replace(/~1/g, '/').replace(/~0/g, '~')
}

export function fieldLabel(name: string, translate: Translate, title?: string) {
    const label = fieldLabels[normalizedField(name)]

    return label ? translate(label) : words(title || name)
}

export function outputLabel(path: string, translate: Translate) {
    return path
        .split('/')
        .slice(2)
        .filter((part) => part !== '*' && part !== 'response_data')
        .map((part) => fieldLabel(unescapePointer(part), translate))
        .join(' · ')
}

export function outputSummary(paths: string[], translate: Translate) {
    const groups = new Map<string, string[]>()

    for (const path of new Set(paths)) {
        const parts = path.split('/')
        let leaf = parts.pop()!

        while (leaf === '*') {
            leaf = parts.pop()!
        }

        const parent = parts.join('/')
        const fields = groups.get(parent) ?? []

        fields.push(fieldLabel(unescapePointer(leaf), translate))
        groups.set(parent, fields)
    }

    return [...groups.entries()]
        .map(([parent, fields]) => {
            const label = outputLabel(parent, translate)

            return label ? `${label}: ${fields.join(', ')}` : fields.join(', ')
        })
        .join('; ')
}

export function displayDate(key: string, value: string, locale: string) {
    if (!dateFields.includes(normalizedField(key)) || !naiveDateTimePattern.test(value)) {
        return value
    }

    const date = new Date(`${value}Z`)

    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 16) !== value.slice(0, 16)) {
        return value
    }

    // Preserve the configured wall-clock value. Never guess an offset for naive timestamps.
    return new Intl.DateTimeFormat(locale.replace('_', '-'), {
        dateStyle: 'medium',
        timeStyle: date.getUTCSeconds() ? 'medium' : 'short',
        timeZone: 'UTC',
    }).format(date)
}

function isResourceSetting(name: string) {
    const normalized = normalizedField(name)

    return resourceFieldPattern.test(normalized) && !limitFieldPattern.test(normalized)
}

export function fixedSettingSummary(tool: ToolSelection): FixedSetting[] {
    const schema = inputSchema(tool)
    const fixed = JSON.parse(tool.fixedArgumentsJson) as Record<string, unknown>
    const dynamic = conversationFields(tool)

    const defaults = Object.fromEntries(
        Object.entries(schema.properties ?? {})
            .filter(([key, field]) => field.default !== undefined && isSummarizedDefault(key, dynamic))
            .map(([key, field]) => [key, field.default])
    )

    return Object.entries({ ...defaults, ...fixed })
        .filter(([key, value]) => isWorthShowing(key, value, schema))
        .map(([key, value]) => ({
            key,
            title: schema.properties?.[key]?.title,
            value,
        }))
}

function isSummarizedDefault(key: string, dynamic: string[]) {
    return !dynamic.includes(key) && (isResourceSetting(key) || timeZoneFields.includes(key))
}

// Resources and time zones always show; other settings only when they differ from the default.
function isWorthShowing(key: string, value: unknown, schema: ToolInputSchema) {
    return (
        isResourceSetting(key) ||
        timeZoneFields.includes(normalizedField(key)) ||
        JSON.stringify(value) !== JSON.stringify(schema.properties?.[key]?.default)
    )
}

export function settingValue(value: unknown): string {
    if (Array.isArray(value)) {
        return value.map(settingValue).join(', ')
    }

    if (value && typeof value === 'object') {
        return Object.entries(value)
            .map(([key, item]) => `${words(key)}: ${settingValue(item)}`)
            .join('; ')
    }

    return value === null ? '—' : String(value)
}

export function approveCapability(tools: ToolSelection[], approved: boolean) {
    return tools.map((tool) => ({
        ...tool,
        policyJson: JSON.stringify({ ...actionPolicy(tool), approved }),
    }))
}
